import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  Provider,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatCalendar } from '@angular/material/datepicker';
import { DATE_FORMATS } from '@app/eds/constants';
import {
  IMultiDatePanelItem,
  IMultipleDateConfigiration
} from '@app/eds/models/date-picker.model';
import { DateUtils } from '@app/eds/utils';
import { CalendarEmptyHeaderComponent } from '../calendar-header/calendar-header.component';

const CUSTOM_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MultipleDatePickerComponent),
  multi: true
};

@Component({
  selector: 'app-multiple-date-picker',
  templateUrl: './multiple-date-picker.component.html',
  styleUrls: ['./multiple-date-picker.component.scss'],
  providers: [CUSTOM_VALUE_ACCESSOR]
})
export class MultipleDatePickerComponent
  implements OnChanges, OnInit, ControlValueAccessor {
  @ViewChild('yearCalendar', { static: false }) yearCalendar!: MatCalendar<any>;

  // Date Ranges configurations
  @Input() public dateRanges!: Date[];

  // configuration for setting date picker from outside component.
  @Input() public configuration!: IMultipleDateConfigiration;

  // Header class names
  @Input() public headerClassNames!: string | string[];

  // content class names
  @Input() public contentClassNames!: string | string[];

  // Mode of values can manage selected data is multiple or just single
  @Input() public multiDateValues: boolean = true;

  // an Formatter default.
  @Input() public formatterTemplate = DATE_FORMATS;

  // min date can be selected
  @Input() public minDate!: Date;

  // Max Date can be selected
  @Input() public maxDate!: Date;

  // a properter to receive data from outside by Input
  @Input() public set selected(_value: Date | string) {
    this.writeValue(_value);
  }

  @Input() public showCentralBorder: boolean = false;

  // The event emittion for emit value changes
  @Output() public changeDate = new EventEmitter<Date>();

  // Date Panels for generate number of Date picker.
  public datePickerPanels: Array<IMultiDatePanelItem> = [];

  // A property contains the empty Calendar Header
  public calendarHeaderComponent = CalendarEmptyHeaderComponent;

  // Function to switch view months mode view years mode
  public multiCalendarViewMode: boolean = true;

  // A Flag to disale the back navigation button
  public disablePrevNavButton: boolean = false;

  // A flag to disable the next navigation button
  public disableNextNavButton: boolean = false;

  public disabled: boolean = false;

  // is a value to manage and communicate with component outside
  private _date!: Date;

  // Set default start date as today for start to render panel from current months
  private _startFromDate: Date = new Date();

  // A number of panels need to render.
  private _numberPanels: number = 2;

  constructor() { }

  /**
   * Angular Lifecyle a call back methods for detect change of Input Property
   * @param {SimpleChanges} changes a Object contains new value and old value of Input property
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes) {
      return;
    }

    if (changes['minDate'] && this.minDate) {
      this.shouldDisablePrevNavButton();
    }

    if (changes['maxDate'] && this.maxDate) {
      this.shouldDisableNextNavButton();
    }
  }

  /**
   * Called after the constructor, initializing input properties, and the first call to ngOnChanges.
   *
   */
  public ngOnInit(): void {
    if (!this.minDate && !this.values) {
      this._startFromDate = new Date();
    } else {
      this._startFromDate = this.minDate || this.values;
    }

    this.assignDatePicker(this._startFromDate);
    this.shouldDisablePrevNavButton();
    this.shouldDisableNextNavButton();
  }

  public get values(): Date {
    return this._date;
  }

  public set values(_value: Date) {
    this._date = _value;

    this.onTouched();
    this.onValueChanges(this._date);
    this.changeDate.emit(this._date);
  }

  public get customCellClasses() {
    return (date: Date): string => {
      return this.configuration && this.configuration.cellCutomClasses
        ? this.customCellClasses(date)
        : '';
    };
  }

  private onTouched = () => { };

  private onValueChanges = (value: Date) => { };

  /**
   * This function is a gateway import value at the first time getting data from outside. Convert to unique type as Date.
   * @param {string[] | Date[]} _value a value pasted from outisde component
   * @returns void
   */
  public writeValue(_value: string | Date): void {
    if (!_value) {
      return;
    }

    if (typeof _value === 'string') {
      this._date = new Date(_value);
      return;
    }

    this._date = _value as Date;
  }

  /**
   * a function navigate to previous months
   * @returns void
   */
  public previousMonthClicked(): void {
    if (!this.multiCalendarViewMode) {
      return;
    }

    this._startFromDate = DateUtils.prevMonthOfDate(this._startFromDate, 1);
    this.assignDatePicker(this._startFromDate);
    this.shouldDisablePrevNavButton();
    this.shouldDisableNextNavButton();
  }

  /**
   * a function navigate to next months
   * @returns void
   */
  public nextMonthClicked(): void {
    if (!this.multiCalendarViewMode) {
      return;
    }

    this._startFromDate = DateUtils.nextMonthOfDay(this._startFromDate, 1);
    this.assignDatePicker(this._startFromDate);
    this.shouldDisableNextNavButton();
    this.shouldDisablePrevNavButton();
  }

  public registerOnChange(fn: (...params: any) => void): void {
    this.onValueChanges = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public onChangeDates(date: Date | null): void {
    // un-sure about this case, when select data the output is different null
    if (!date) {
      return;
    }

    this.values = date;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public get startActiveDate(): Date {
    return this._startFromDate;
  }

  public get endActiveDate(): Date {
    const endDate = DateUtils.nextMonthOfDay(this._startFromDate, this._numberPanels - 1);
    return endDate;
  }

  /**
   * switch to year view calendar
   */
  public switchToYearMode(): void {
    this.multiCalendarViewMode = false;
  }

  /**
   * set start a date month picker and switch to multi select date mode
   */
  public onYearModeChooseMonth(data: any): void {
    this.multiCalendarViewMode = true;
    this._startFromDate = data;

    this.assignDatePicker(this._startFromDate);
    this.shouldDisablePrevNavButton();
    this.shouldDisableNextNavButton();
  }

  /**
   * Assigned data for picker will start from
   * current Date or current Selected Date to next
   * monthbase on number of number Panles are setting
   * @param {Date[]} arrayDates Array of Value need to assign to picker
   */
  private assignDatePicker(startFrom: Date): void {
    let i = 1;

    this.datePickerPanels = [
      {
        date: startFrom,
        dateFilter: (date: Date) =>
          DateUtils.isDateBelongToRanges(date, this.minDate, this.maxDate)
      }
    ];

    while (i < this._numberPanels) {
      const _dateConfig = this.createSelectedDateValue(i);
      this.datePickerPanels.push(_dateConfig);
      i++;
    }
  }

  /**
   * A function for finding next month of new selected picker
   * @param numberToNextMonth the number need to plus to next months
   * @returns Date
   */
  private createSelectedDateValue(numberToNextMonth: number): any {
    const prevDateValue = this.datePickerPanels[numberToNextMonth - 1];
    const date = DateUtils.nextMonthOfDay(prevDateValue.date);

    return {
      date: date,
      dateFilter: (_date: Date) =>
        DateUtils.isDateBelongToRanges(_date, this.minDate, this.maxDate)
    };
  }

  /**
   * a function for checking the status of previous navigation button by min Date
   */
  private shouldDisablePrevNavButton(): void {
    // Return if minDate is undefined (caused of optional)
    if (!this.minDate) {
      return;
    }
    this.disablePrevNavButton =
      DateUtils.comparedOnlyDates(this._startFromDate, this.minDate) < -1;
  }

  /**
   * a function for checking the status of next navition button by max Date
   */
  private shouldDisableNextNavButton(): void {
    // Return if maxDate is undefined (caused of optional)
    if (!this.maxDate) {
      return;
    }

    const endDate = DateUtils.nextMonthOfDay(this._startFromDate, this._numberPanels - 1);
    this.disableNextNavButton = DateUtils.comparedOnlyDates(endDate, this.maxDate) > -1;
  }
}
